/**
 * `@sukha/shared` — the DB-free half of the domain.
 *
 * Everything here is importable from a Client Component, a route handler, the
 * desktop app or the extension without pulling Drizzle, `pg`, Stripe, `sharp`
 * or a connection pool into the graph. Nothing here may import `@sukha/db`,
 * `drizzle-orm`, or `server-only`.
 *
 * Prefer a subpath import (`@sukha/shared/domain/users`) over this barrel.
 */

export * from "./domain/cartoons.js";
export * from "./domain/chat.js";
export * from "./domain/distraction.js";
export * from "./domain/email.js";
export * from "./domain/enums.js";
export * from "./domain/memberFiles.js";
export * from "./domain/memories.js";
export * from "./domain/smartAssistantSettings.js";
export * from "./domain/taskSettings.js";
export * from "./domain/settingsBounds.js";
export * from "./domain/users.js";
export * from "./focusMessages/constants.js";
export * from "./lib/allowedOrigins.js";
export * from "./lib/trustedMedia.js";
export * from "./lib/storedAttachments.js";
export * from "./lib/focusMessagesInterpolate.js";
export * from "./lib/productivityComparison.js";
export * from "./lib/sessionMissedOpportunities.js";
export * from "./domain/sessionTick.js";
export * from "./domain/devicePresence.js";
export * from "./lib/sessionComparisonConstants.js";
export * from "./lib/sessionProductivityComparison.js";
export * from "./locale/memberUiLocales.js";
export * from "./publicHostUrl.js";
export * from "./upstreamFailure.js";
/*
 * Streak and distraction domain logic, moved out of `apps/web` (TX-4865).
 *
 * `date-fns` is this package's first runtime dependency. That is within its
 * remit: AGENTS.md defines it as the DB-free half of the domain — no
 * `drizzle-orm`, no `@sukha/db` — and date maths is domain logic, not database
 * access. `date-handling.mdc` already makes date-fns the required tool for it.
 */
export * from "./distractionColors.js";
export * from "./streakLogic.js";
export * from "./streak.js";

/*
 * Pure render inputs pulled out of `apps/web` so `@sukha/charts` can reach them
 * (TX-4865). `satori.tsx` needs both to draw a session card, and neither had any
 * dependency beyond this package to begin with — `sessionComparisonCopy` was
 * already importing its own sibling from here.
 */
export * from "./memberDisplayName.js";
export * from "./taskProgressColor.js";
export * from "./sessionComparisonCopy.js";

/*
 * Chart geometry and palette, shared by the ten client chart components in
 * `apps/web` and by `@sukha/charts`'s server renderer (TX-4865). 443 lines with
 * no imports of its own -- it sat under `components/` only because its first
 * caller was a component.
 */
export * from "./chartCommon.js";
export * from "./activityStreak.js";

/*
 * Leaf utilities pulled out of `apps/web` so `@sukha/email` can reach them
 * (TX-4879). All four had **no imports of their own** and consumers on both
 * sides of the app split -- `messageFormatting` is read by the chat UI and by
 * the unread-messages email; `assistantPublicAvatarPath` by five surfaces. They
 * sat under `lib/` only because web was the only app that existed.
 */
export * from "./linkDetection.js";
export * from "./scheduling.js";
export * from "./assistantPublicAvatarPath.js";
export * from "./messageFormatting.js";

/* Tolt referral attribution — pure, and read by Client Components (TX-4863). */
export * from "./toltAttribution.js";

/*
 * External task services — the dual-use half of the task-adapters layer
 * (TX-4880). `SERVICE_CONFIGS` is read by the server adapters in `@sukha/tasks`
 * *and* by six Client Components, so it cannot live beside modules that import
 * `server-only`. The due-date parser is client-only today and pure either way.
 */
export * from "./domain/taskServices.js";
export * from "./lib/externalTaskDates.js";

/*
 * Pulled out of `apps/web` so `@sukha/assistant` can reach them (TX-4864).
 * `taskTypes` is 37 pure lines with 76 consumers — it sat under `types/` only
 * because web was the only app. `timeUtils` needs `parse-duration`, which is a
 * runtime dep of the same kind as date-fns here.
 */
export * from "./taskTypes.js";
export * from "./timeUtils.js";

/*
 * Pure helpers that used to sit in `apps/web/src/{utils,lib/server}` because web
 * was the only caller. TX-4864 moved the assistant routers to `apps/api`, and
 * every one of these now has consumers in BOTH apps — so they live in one place
 * rather than being copied, which is the whole point of this package.
 *
 * `perUserRateLimit` and `applyBatch` carried `import "server-only"`. That marker
 * is a Next bundler affordance and THROWS outside Next, so it cannot travel into
 * a package `apps/api` imports. Both are pure functions with no ambient state
 * beyond an in-memory Map, so nothing about them needed the marker to be true.
 */
export * from "./greetingUtils.js";
export * from "./perUserRateLimit.js";
export * from "./applyBatch.js";
export * from "./sentrySampling.js";
export * from "./sentryEnvironment.js";
export * from "./sentryPrivacy.js";
export * from "./sentrySpans.js";
export * from "./sentryScrub.js";
export * from "./publicBaseUrl.js";
export * from "./routeErrorEnvelope.js";
export * from "./buildInfo.js";
